import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { Grid } from '@mui/material';
import InfoText from './CashlesInfoText';
import { contractNameMapper, serviceNameMapper } from './CashlesInfoText';
import CashlessDocuments from './CashlessDocuments';

const Div = styled('div')(({ theme }) => ({
  ...theme.typography.button,
  color: theme.palette.primary.main,
  padding: theme.spacing(1)
}));

function TypographyButton({ labelText }) {
  return <Div>{labelText}</Div>;
}

export default function CashlessRequestFinancials({ requestDetails }) {
  const [expanded, setExpanded] = React.useState('panel1');
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const documentDetails = [];
  if (requestDetails.bills) {
    documentDetails.push({
      doctype: 'Medical Bills',
      key: requestDetails.bills[0],
      service: serviceNameMapper(requestDetails.service_type.toString()),
      requestid: requestDetails.requestid,
      orderid: requestDetails.orderid
    });
  }

  if (requestDetails.invoices) {
    documentDetails.push({
      doctype: 'Medical Invoices',
      key: requestDetails.invoices[0],
      service: serviceNameMapper(requestDetails.service_type.toString()),
      requestid: requestDetails.requestid,
      orderid: requestDetails.orderid
    });
  }

  if (requestDetails.reports) {
    documentDetails.push({
      doctype: 'Lab Test Reports',
      key: requestDetails.reports[0],
      service: serviceNameMapper(requestDetails.service_type.toString()),
      requestid: requestDetails.requestid,
      orderid: requestDetails.orderid
    });
  }

  if (requestDetails.prescriptions) {
    documentDetails.push({
      doctype: 'Medicine Prescriptions',
      key: requestDetails.prescriptions[0],
      service: serviceNameMapper(requestDetails.service_type.toString()),
      requestid: requestDetails.requestid,
      orderid: requestDetails.orderid
    });
  }

  return (
    <div>
      {documentDetails.length > 0 ? (
        <Accordion
          expanded={expanded === 'panel4'}
          onChange={handleChange('panel4')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel4bh-content"
            id="panel4bh-header"
          >
            <TypographyButton
              sx={{ width: '33%', flexShrink: 0 }}
              labelText={'Fullfilment Documents'}
            />
          </AccordionSummary>
          <AccordionDetails>
            <CashlessDocuments docDetails={documentDetails} />
          </AccordionDetails>
        </Accordion>
      ) : null}

      <Accordion
        expanded={expanded === 'panel1'}
        onChange={handleChange('panel1')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          <TypographyButton
            sx={{ width: '33%', flexShrink: 0 }}
            labelText={'Patient Details'}
          />
        </AccordionSummary>
        <AccordionDetails>
          <InfoText
            keyValuePairs={[
              {
                key: 'Patient Name',
                value: requestDetails.name || null
              },
              {
                key: 'Relation To Employee',
                value: requestDetails.relation || null
              },
              {
                key: 'Employee Code',
                value: requestDetails.employeeid || null
              },
              {
                key: 'Employee Email',
                value: requestDetails.email || null
              },
              {
                key: 'Phone Number',
                value: requestDetails.phonenumber || null
              },
              {
                key: 'Age',
                value: requestDetails.age || null
              },
              {
                key: 'Gender',
                value: requestDetails.gender || null
              }
            ].filter((ele) => ele.value != null)}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel2'}
        onChange={handleChange('panel2')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel2bh-content"
          id="panel2bh-header"
        >
          <TypographyButton
            sx={{ width: '33%', flexShrink: 0 }}
            labelText={'Product Details'}
          />
        </AccordionSummary>
        <AccordionDetails>
          <InfoText
            keyValuePairs={[
              {
                key: 'Product Type',
                value: requestDetails.service_type
                  ? contractNameMapper(
                      requestDetails.service_type.toString(),
                      'Miscellaneous'
                    )
                  : null
              },
              {
                key: 'Cart Items',
                value: requestDetails.cart_items || null
              },
              {
                key: 'Purchase Date',
                value: requestDetails.createdon
                  ? new Date(requestDetails.createdon).toLocaleDateString()
                  : null
              },
              {
                key: 'Fullfilment Date',
                value: requestDetails.fullfilledon
                  ? new Date(requestDetails.fullfilledon).toLocaleDateString()
                  : null
              }
            ].filter((ele) => ele.value != null)}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel3'}
        onChange={handleChange('panel3')}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel3bh-content"
          id="panel3bh-header"
        >
          <TypographyButton
            sx={{ width: '33%', flexShrink: 0 }}
            labelText={'Payment Details'}
          />
        </AccordionSummary>
        <AccordionDetails>
          <InfoText
            keyValuePairs={[
              {
                key: 'Cart Items',
                value: requestDetails.cart_items || null
              },
              {
                key: 'Cart Item Price',
                value: requestDetails.total_amount
                  ? '₹ ' + requestDetails.total_amount.toString() + '/-'
                  : null
              },
              {
                key: 'Cashless Amount',
                value: requestDetails.total_cashless_amount
                  ? '₹ ' +
                    requestDetails.total_cashless_amount.toString() +
                    '/-'
                  : null
              },
              {
                key: 'Payment Gateway Amount',
                value: requestDetails.total_pg_amount
                  ? '₹ ' + requestDetails.total_pg_amount.toString() + '/-'
                  : null
              },
              {
                key: 'Coupon Amount',
                value: requestDetails.total_coupon_amount
                  ? '₹ ' + requestDetails.total_coupon_amount.toString() + '/-'
                  : null
              },
              {
                key: 'OrderId',
                value: requestDetails.orderid || null
              },
              {
                key: 'RequestId',
                value: requestDetails.requestid || null
              }
            ].filter((ele) => ele.value != null)}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
